import ResponsiveModal from "@/modules/common/components/ui-kit/modals/responsive-modal";
import { Box, Button, Stack, TextField, Typography, useTheme } from "@mui/material";
import { CircleAlert, Landmark } from "lucide-react";
import { useEffect, useState } from "react";
import { NoteAttachmentContext } from "../dtos";
import {
  destructiveButtonSx,
  fieldInputSx,
  primaryButtonSx,
  secondaryButtonSx,
} from "./field-shell";
import {NoteAttachmentComposer, useNoteAttachmentDraft} from "./note-attachments";

/**
 * §Modals: the confirmation shell the Figma flow reuses (canvas 11508:321522, section "25 Jul") —
 * title + ✕ · consequence copy (plain, or in a negative alert band) · optional tinted amount panel ·
 * italic small print · footer [Cancel] [<verb>]. Verified against frames
 * 12107:64726 "Submit extra documents", 12107:60805 "Accept loan?", 12107:45495 "Request guarantor".
 *
 * It also replaces `window.prompt` for the engine's mandatory comment: an action carrying
 * `requiresComment` (or a REJECTED outcome on a comment-gated step) is confirmed here, so the agent
 * sees WHAT the action does before typing the reason the engine demands.
 */
export interface ActionConfirmRequest {
  title: string;
  /** the consequence sentence, verbatim from the design where the design has one */
  message?: string;
  /** design puts the guarantor consequence in a negative alert band instead of plain body copy */
  alert?: boolean;
  /** design's positive-tinted "Loan amount" panel under the message (the accept-loan modal) */
  amount?: number;
  amountLabel?: string;
  /** italic small print under the body ("* You can change this later if …") */
  footnote?: string;
  /** collect the engine-mandated comment in the modal rather than a browser prompt */
  commentRequired?: boolean;
  /**
   * §Terminate: three-state comment collection — 'optional' renders the field without gating Confirm.
   * Wins over the legacy boolean when both are set; `commentRequired: true` still means 'required'.
   */
  commentMode?: "none" | "optional" | "required";
  commentLabel?: string;
  confirmLabel?: string;
  /**
   * Design pattern: a modal that COLLECTS something (comment, guarantor details, employment type)
   * offers only its primary verb — the ✕ is the way out. Pure confirmations keep Cancel.
   */
  hideCancel?: boolean;
  /** REJECTED / send-back actions get the destructive treatment, matching the step's own buttons */
  destructive?: boolean;
}

const egp = (n: number): string => `${Math.round(n).toLocaleString("en-US")} EGP`;

const ActionConfirmModal = ({
  request,
  busy,
  caseUid,
  attachmentContext,
  onCancel,
  onConfirm,
}: {
  /** null ⇒ closed. Set it to open the modal for one action. */
  request: ActionConfirmRequest | null;
  busy: boolean;
  caseUid?: string | null;
  attachmentContext?: NoteAttachmentContext | null;
  onCancel: () => void;
  /** comment is undefined unless the request asked for one */
  onConfirm: (comment?: string, attachmentUids?: string[]) => void | Promise<unknown>;
}) => {
  const theme = useTheme();
  const c = theme.nawy.colors.semantic;
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const attachmentDraft = useNoteAttachmentDraft({caseUid, context: attachmentContext});

  // a fresh request must never inherit the previous action's reason
  useEffect(() => setComment(""), [request]);

  const commentMode =
    request?.commentMode ?? (request?.commentRequired ? "required" : "none");
  const needsComment = commentMode !== "none";
  const canConfirm =
    !busy &&
    !submitting &&
    !attachmentDraft.pending &&
    !attachmentDraft.hasErrors &&
    (commentMode !== "required" || comment.trim().length > 0);
  const cancel = () => {
    if (busy || submitting) return;
    attachmentDraft.discard();
    onCancel();
  };
  const confirm = async () => {
    if (!canConfirm) return;
    setSubmitting(true);
    try {
      await onConfirm(
        needsComment && comment.trim() ? comment.trim() : undefined,
        attachmentDraft.attachmentUids
      );
      attachmentDraft.markSubmitted();
      onCancel();
    } catch {
      // The owning mutation surfaces its error. Keep the written reason and staged files for retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <ResponsiveModal
      open={request !== null}
      onClose={cancel}
      title={request?.title ?? ""}
    >
      {/* §MobileQA: MobileModal already wraps every child in a spacing.md (16px) box, so the
          sheet body was inset 36px instead of the design's 16 (318px of content at a 390px
          viewport against the design's 343). Only the desktop shell (CommonModal) pads nothing
          of its own, so the 20px inset stays there. */}
      {/* §MobileQA: the bottom inset is NOT symmetric with the other three. Every other sheet
          gets its gap for free — MobileModal renders ModalFooter outside Sheet.Scroller, so the
          scroller's own 16px bottom padding separates them. This modal brings its own footer as a
          child, i.e. INSIDE the scroller and directly after this box, so a mobile inset of 0 put
          the note field's bottom border flush on the footer divider — the two rules read as one
          thick line. 16px here matches the scroller's top inset, so the body sits evenly between
          the header and the divider. Desktop's 20px already did this. */}
      <Box
        sx={{
          px: { mobile: 0, desktop: 2.5 },
          pt: { mobile: 0, desktop: 2.5 },
          pb: { mobile: 2, desktop: 2.5 },
          width: { mobile: "100%", desktop: 480 },
        }}
      >
        {request?.message &&
          (request.alert ? (
            // design (node 12107:46068, "Request guarantor"): the band is px 16 / py 12 with a
            // 16px glyph 8px from the copy — measured 343x78 with the text block 291x54.
            <Stack
              direction="row"
              spacing={1}
              alignItems="flex-start"
              sx={{
                px: 2,
                py: 1.5,
                borderRadius: theme.nawy.radius.lg,
                backgroundColor: c.layer.extension.negativeLight,
              }}
            >
              <CircleAlert
                size={16}
                color={c.text.extension.negative}
                style={{ flexShrink: 0, marginTop: 1 }}
              />
              <Typography
                sx={{
                  // Body/sm - Regular 12/400/18 — the copy wraps to the design's 3 lines at
                  // 291px wide. 14/20/600 grew the band a whole line taller and read as a
                  // shout next to the sheet's own 20px title.
                  fontSize: 12,
                  lineHeight: "18px",
                  fontWeight: 400,
                  color: c.text.extension.negative,
                }}
              >
                {request.message}
              </Typography>
            </Stack>
          ) : (
            <Typography sx={{ fontSize: 14, lineHeight: "20px", color: c.text.primary }}>
              {request.message}
            </Typography>
          ))}

        {request?.amount != null && (
          <Stack
            direction="row"
            spacing={1.5}
            alignItems="center"
            sx={{
              mt: 2,
              p: 1.5,
              borderRadius: theme.nawy.radius.lg,
              backgroundColor: c.layer.extension.positiveLight,
            }}
          >
            <Landmark size={20} color={c.text.extension.positive} />
            <Box>
              <Typography sx={{ fontSize: 11, lineHeight: "16px", color: c.text.secondary }}>
                {request.amountLabel ?? "Loan amount"}
              </Typography>
              <Typography
                sx={{
                  fontSize: 16,
                  lineHeight: "24px",
                  fontWeight: 700,
                  color: c.text.extension.positive,
                }}
              >
                {egp(request.amount)}
              </Typography>
            </Box>
          </Stack>
        )}

        {request?.footnote && (
          <Typography
            sx={{
              mt: 1.5,
              fontSize: 11,
              lineHeight: "16px",
              color: c.text.tertiary,
            }}
          >
            {request.footnote}
          </Typography>
        )}

        {needsComment && (
          <Box sx={{ mt: 2 }}>
            <Typography
              sx={{ fontSize: 11, lineHeight: "16px", color: c.text.secondary, mb: 0.5 }}
            >
              {request?.commentLabel ?? "Comment"}
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={4}
              autoFocus
              placeholder={request?.commentLabel ?? "Comment"}
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              sx={fieldInputSx(theme, { multiline: true })}
            />
            {attachmentContext && (
              <NoteAttachmentComposer draft={attachmentDraft} disabled={busy || submitting} />
            )}
          </Box>
        )}
      </Box>

      {/* §MobileQA: the footer is a child of MobileModal's padded box too, so its divider was
          inset 16px each side instead of running edge to edge as every sheet frame draws it.
          The negative margin cancels exactly the sheet's own 16px (never more — a wider pull
          would open a horizontal scrollbar on the sheet), then px 2 puts the buttons back on
          the design's 16px inset. Desktop keeps its own 20px, no cancelling needed. */}
      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{
          mx: { mobile: -2, desktop: 0 },
          px: { mobile: 2, desktop: 2.5 },
          py: 2,
          borderTop: `1px solid ${c.border.secondary}`,
        }}
      >
        {!request?.hideCancel && (
          <Button
            variant="outlined"
            disabled={busy || submitting}
            onClick={cancel}
            sx={secondaryButtonSx(theme)}
          >
            Cancel
          </Button>
        )}
        <Button
          variant={request?.destructive ? "outlined" : "contained"}
          disableElevation
          disabled={!canConfirm}
          onClick={() => void confirm()}
          sx={request?.destructive ? destructiveButtonSx(theme) : primaryButtonSx(theme)}
        >
          {request?.confirmLabel ?? "Accept"}
        </Button>
      </Stack>
    </ResponsiveModal>
  );
};

export default ActionConfirmModal;
